import { useDisclosure } from '@chakra-ui/react';
import { IAccountRespSchema, IPayment } from '@monetize/types/app';
import { useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MBox,
  MButton,
  MFlex,
  MHStack,
  MPageHeader,
} from '../../../components/Monetize';
import { useTableColumns } from '../../../components/Monetize/DataTableNew/columnCatalog/useTableColumns';
import { DataTable } from '../../../components/Monetize/DataTableNew/DataTable';
import { ACTION_COLUMN_KEY } from '../../../components/Monetize/DataTableNew/dataTableNewRenderers';
import { DataTableRef } from '../../../components/Monetize/DataTableNew/DataTableTypes';
import { ExportEntityButton } from '../../../components/Monetize/ExportEntityButton';
import { RefreshTableButton } from '../../../components/Monetize/RefreshTableButton';
import { getAccountPaymentView } from '../../../constants/routes';
import { useAccountPayments } from '../../../hooks/AccountDetail';
import { useACL } from '../../../services/acl/acl';
import { ACLCheck } from '../../../services/acl/ACLCheck';
import { navigateOrOpenNewWindow } from '../../../utils/misc';
import { AccountManualPaymentModal } from './AccountManualPaymentModal';
import { PaymentActions } from './PaymentActions';
import {
  PAYMENT_LIST_CATALOG,
  PaymentListColumnContext,
} from './paymentList.columns';

export { PaymentListStatusTagStyle } from './paymentList.columns';

interface PaymentListProps {
  account: IAccountRespSchema;
  billGroupId?: string;
}

export const PaymentList = ({ account, billGroupId }: PaymentListProps) => {
  const navigate = useNavigate();
  const { id: accountId } = account;
  const { canDo } = useACL();

  const {
    open: isManualPaymentModalOpen,
    onOpen: onManualPaymentModalOpen,
    onClose: onManualPaymentModalClose,
  } = useDisclosure();

  const {
    filters,
    setFilters,
    onResetFilter,
    totalRecords,
    setPager,
    pager,
    loading,
    listData,
    refetch,
  } = useAccountPayments({ accountId });

  const handleManualPaymentClose = (_reload: boolean) => {
    onManualPaymentModalClose();
  };

  const tableRef = useRef<DataTableRef>(null);

  const actionBodyTemplate = useCallback(
    (rowData: IPayment) => (
      <PaymentActions
        payment={rowData}
        accountId={accountId}
        refetch={refetch}
      />
    ),
    [accountId, refetch],
  );

  const columnContext = useMemo<PaymentListColumnContext>(
    () => ({ account, actionBodyTemplate }),
    // Only `defaultCurrency` is consumed by the column cells.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [account?.defaultCurrency, actionBodyTemplate],
  );
  const { columns, state: columnState } = useTableColumns(
    PAYMENT_LIST_CATALOG,
    columnContext,
  );

  const handleResetFilter = () => {
    onResetFilter();
    tableRef.current?.resetFilters();
  };

  const hasFilerApplied = filters && filters?.length > 0;
  const hasContent = listData?.content?.length > 0;
  const shouldShowHeaderContent = hasContent || hasFilerApplied;

  return (
    <MBox>
      <MPageHeader title="Payments" hideContent={!shouldShowHeaderContent}>
        <MFlex>
          <MHStack gap="2">
            <RefreshTableButton refetch={refetch} />
            <ExportEntityButton
              entity="payments"
              filters={filters}
              sortField={pager.sortField}
              sortOrder={pager.sortOrder}
              endpointParams={[accountId]}
              getFilename={() => `payments-${accountId}.csv`}
            />
            <ACLCheck acls={[['billing', 'create']]}>
              <MButton variant="secondary" onClick={onManualPaymentModalOpen}>
                New Payment
              </MButton>
            </ACLCheck>
          </MHStack>
        </MFlex>
      </MPageHeader>

      <DataTable
        ref={tableRef}
        columns={columns}
        state={columnState}
        data={listData?.content ?? []}
        pinnedRightColumns={[ACTION_COLUMN_KEY]}
        totalElements={totalRecords}
        totalPages={listData?.totalPages}
        pager={pager}
        setPager={setPager}
        initialSortingState={[{ id: 'paymentDate', desc: true }]}
        initialFilters={filters}
        onFiltersChange={(newFilters) => setFilters(newFilters)}
        onRowClick={(row, _tableRow, openInNewWindow) => {
          if (!row.id) {
            return;
          }

          return navigateOrOpenNewWindow(
            navigate,
            getAccountPaymentView(accountId, row.id),
            openInNewWindow,
          );
        }}
        isLoading={loading}
        emptyProps={{
          mainMessage: 'Looks like there are no payments here.',
          smallMessage: 'When a payment is made it will appear here.',
          btnLabel: canDo([['billing', 'create']]) ? 'New Payment' : '',
          onClick: onManualPaymentModalOpen,
        }}
        emptyFilterResultsProps={{ btnLabel: 'Reset Filters' }}
        resetFilter={handleResetFilter}
      />
      {isManualPaymentModalOpen && (
        <AccountManualPaymentModal
          accountId={accountId}
          accountCurrency={account.defaultCurrency}
          isOpen={isManualPaymentModalOpen}
          onClose={handleManualPaymentClose}
        />
      )}
    </MBox>
  );
};
